
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	makeEnumFetchFunction,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserNevdisSearch from 'Models/Security/Acl/UserNevdisSearch';
import VisitorsNevdisSearch from 'Models/Security/Acl/VisitorsNevdisSearch';
import * as Enums from '../Enums';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface INevdisSearchEntityAttributes extends IModelAttributes {
	request: string;
	response: string;
	status: Enums.processingStatus;

	searchRequestId: string;
	searchRequest: Models.SearchRequestEntity | Models.ISearchRequestEntityAttributes;

}

@entity('NevdisSearchEntity', 'Nevdis Search')
export default class NevdisSearchEntity extends Model
	implements INevdisSearchEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserNevdisSearch(),
		new VisitorsNevdisSearch(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<NevdisSearchEntity, string>()
	@CRUD({
		name: 'Request',
		displayType: 'textarea',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public request: string;

	@observable
	@attribute<NevdisSearchEntity, string>()
	@CRUD({
		name: 'Response',
		displayType: 'textarea',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public response: string;

	@observable
	@attribute<NevdisSearchEntity, Enums.processingStatus>()
	@CRUD({
		name: 'Status',
		displayType: 'enum-combobox',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.processingStatusOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.processingStatusOptions),
		displayFunction: (attr: Enums.processingStatus) => Enums.processingStatusOptions[attr],
	})
	public status: Enums.processingStatus;
	

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Search Request',
		displayType: 'reference-combobox',
		order: 40,
		referenceTypeFunc: () => Models.SearchRequestEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'searchRequests',
			oppositeEntity: () => Models.SearchRequestEntity,
		}),
		
	})
	public searchRequestId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public searchRequest: Models.SearchRequestEntity;



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<INevdisSearchEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<INevdisSearchEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.request !== undefined) {
				this.request = attributes.request;
			}
			if (attributes.response !== undefined) {
				this.response = attributes.response;
			}
			if (attributes.status !== undefined) {
				this.status = attributes.status;
			}
			if (attributes.searchRequestId !== undefined) {
				this.searchRequestId = attributes.searchRequestId;
			}
			if (attributes.searchRequest !== undefined) {
				if (attributes.searchRequest === null) {
					this.searchRequest = attributes.searchRequest;
				} else if (attributes.searchRequest instanceof Models.SearchRequestEntity) {
					this.searchRequest = attributes.searchRequest;
					this.searchRequestId = attributes.searchRequest.id;
				} else {
					this.searchRequest = new Models.SearchRequestEntity(attributes.searchRequest);
					this.searchRequestId = this.searchRequest.id;
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		searchRequest {
			${Models.SearchRequestEntity.getAttributes().join('\n')}
			${Models.SearchRequestEntity.getFiles().map(f => f.name).join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'searchRequest',
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(NevdisSearchEntity.prototype, 'created');
CRUD(modifiedAttr)(NevdisSearchEntity.prototype, 'modified');
